/**
 * AcquireOS Workflow Engine
 * Pure function: takes dealState → returns array of actionable alerts.
 * No side-effects. Called whenever dealState changes.
 */

// ── Helpers ────────────────────────────────────────────────────────────────────
const daysBetween = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr + 'T12:00:00') - new Date();
  return Math.round(diff / 86_400_000);
};

const hasField = (obj, path) => {
  if (!obj) return false;
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null || cur[p] == null) return false;
    cur = cur[p];
  }
  return true;
};

let _counter = 1;
const alert = (overrides) => ({
  id: `wf_${overrides.moduleNumber}_${overrides.category}_${_counter++}`,
  read: false,
  dismissible: true,
  persistent: false,
  createdAt: new Date().toISOString(),
  ...overrides,
});

// ── Main export ────────────────────────────────────────────────────────────────
export function generateWorkflowAlerts(dealState = {}) {
  _counter = 1; // reset so IDs are stable per call
  const alerts = [];
  const stage = Number(dealState.pipelineStage) || 0;

  // ── MODULE 1: Deal Lead Generation ────────────────────────────────────────
  if (!dealState.dealId || !hasField(dealState, 'rawDealData.price')) {
    alerts.push(alert({
      moduleNumber: 1,
      severity: 'urgent',
      category: 'decision',
      persistent: true,
      dismissible: false,
      title: 'Select a deal to begin',
      message: 'No deal has been selected yet. Browse the deal list in Module 1, review AI scores and fit ratings, then confirm your selection to unlock the full acquisition pipeline.',
      actionText: 'Go to Deal Lead Generation',
    }));
  } else if (!hasField(dealState, 'rawDealData.source')) {
    alerts.push(alert({
      moduleNumber: 1,
      severity: 'warning',
      category: 'incomplete',
      title: 'Deal source not recorded',
      message: 'The deal source (broker, listing platform, etc.) is missing. Fill this in Module 1 for accurate reporting.',
      actionText: 'Complete Module 1',
    }));
  }

  // ── MODULE 2: Market Intelligence ─────────────────────────────────────────
  if (stage >= 1 && !hasField(dealState, 'marketAnalysis.marketScore')) {
    alerts.push(alert({
      moduleNumber: 2,
      severity: 'warning',
      category: 'incomplete',
      title: 'Market analysis not run',
      message: "Market intelligence data for this deal's location has not been generated. Run Module 2 to get market score, rent growth, and vacancy signals before underwriting.",
      actionText: 'Run Market Analysis',
    }));
  }

  // ── MODULE 3: Deal Intake ─────────────────────────────────────────────────
  if (stage >= 1) {
    const fin = dealState.extractedFinancials || {};
    const missingDocs = [];
    if (!hasField(fin, 'revenue.totalRevenue'))  missingDocs.push('Operating Memorandum / Income Statement');
    if (!hasField(fin, 'revenue.rentRoll'))       missingDocs.push('Rent Roll');
    if (!hasField(fin, 'expenses.totalExpenses')) missingDocs.push('T12 Expense Report');

    if (missingDocs.length > 0) {
      alerts.push(alert({
        moduleNumber: 3,
        severity: missingDocs.length >= 2 ? 'urgent' : 'warning',
        category: 'missing_doc',
        persistent: missingDocs.length >= 2,
        title: `Missing documents (${missingDocs.length})`,
        message: `The following required documents are missing: ${missingDocs.join(', ')}. Upload or enter these in Module 3 before underwriting can run accurately.`,
        actionText: 'Upload Documents in Module 3',
      }));
    }

    if (!fin.noi && stage >= 2) {
      alerts.push(alert({
        moduleNumber: 3,
        severity: 'warning',
        category: 'incomplete',
        title: 'NOI not extracted',
        message: 'Net Operating Income has not been calculated. Complete the financial intake in Module 3 to enable underwriting and scoring.',
        actionText: 'Complete Financial Intake',
      }));
    }

    if (fin.dataQuality?.score < 70 && fin.dataQuality?.score > 0) {
      alerts.push(alert({
        moduleNumber: 3,
        severity: 'warning',
        category: 'incomplete',
        title: `Data quality score low (${fin.dataQuality.score}/100)`,
        message: `Financial data quality is below the recommended threshold. Missing fields: ${(fin.dataQuality.missingFields || []).join(', ') || 'unknown'}. Review and fill gaps in Module 3.`,
        actionText: 'Improve Data Quality',
      }));
    }
  }

  // ── MODULE 4: Underwriting ────────────────────────────────────────────────
  if (stage >= 3) {
    const uw = dealState.underwritingMetrics || {};
    if (!uw.capRate?.entry) {
      alerts.push(alert({
        moduleNumber: 4,
        severity: 'warning',
        category: 'incomplete',
        title: 'Underwriting not completed',
        message: 'Cap rate, DSCR, and LTV have not been calculated. Run the full underwriting model in Module 4 before scoring this deal.',
        actionText: 'Run Underwriting Engine',
      }));
    } else if (Number(uw.dscr?.current) < 1.20 && Number(uw.dscr?.current) > 0) {
      alerts.push(alert({
        moduleNumber: 4,
        severity: 'urgent',
        category: 'decision',
        title: `DSCR below 1.20x (${Number(uw.dscr.current).toFixed(2)}x)`,
        message: 'Debt Service Coverage Ratio is below the 1.20x minimum lender threshold. Review loan sizing, interest rate assumptions, or NOI projections before advancing to lender matching.',
        actionText: 'Review Underwriting Assumptions',
        persistent: true,
      }));
    } else if (Number(uw.dscr?.current) < 1.25 && Number(uw.dscr?.current) > 0) {
      alerts.push(alert({
        moduleNumber: 4,
        severity: 'warning',
        category: 'decision',
        title: `DSCR near threshold (${Number(uw.dscr.current).toFixed(2)}x)`,
        message: 'DSCR is between 1.20x and 1.25x — some lenders may require additional reserves or a lower LTV. Review assumptions in Module 4.',
        actionText: 'Review DSCR',
      }));
    }
  }

  // ── MODULE 5: Deal Score & Verdict ────────────────────────────────────────
  if (stage >= 4) {
    const so = dealState.scoreAndOffer || {};
    if (!so.numericalScore) {
      alerts.push(alert({
        moduleNumber: 5,
        severity: 'warning',
        category: 'incomplete',
        title: 'Deal score not generated',
        message: 'No AI deal score has been calculated. Run Module 5 to get a score, recommended offer price, and verdict before contacting lenders.',
        actionText: 'Generate Deal Score',
      }));
    } else if (Number(so.numericalScore) < 50) {
      alerts.push(alert({
        moduleNumber: 5,
        severity: 'urgent',
        category: 'decision',
        title: `Low deal score (${so.numericalScore}/100)`,
        message: `This deal scored ${so.numericalScore}/100 — below the recommended threshold of 50. Consider renegotiating the price, revising assumptions, or passing on this deal.`,
        actionText: 'Review Deal Score',
        persistent: true,
      }));
    }
  }

  // ── MODULE 6: Lender Match ────────────────────────────────────────────────
  if (stage >= 5) {
    const lm = dealState.lenderMatches || {};
    if (!lm.matchedLenders?.length) {
      alerts.push(alert({
        moduleNumber: 6,
        severity: 'warning',
        category: 'decision',
        title: 'Lender matches not run',
        message: 'No lenders have been matched to this deal. Run Module 6 to get ranked lender recommendations based on your underwriting metrics.',
        actionText: 'Match Lenders',
      }));
    } else if (!lm.topRecommendation?.lenderName) {
      alerts.push(alert({
        moduleNumber: 6,
        severity: 'urgent',
        category: 'decision',
        persistent: true,
        dismissible: false,
        title: 'Lender selection required',
        message: `${lm.matchedLenders.length} lenders matched. You must select and confirm your preferred lender before the loan package can be generated and submitted.`,
        actionText: 'Select a Lender',
      }));
    }
  }

  // ── MODULE 7: Deal Syndication ────────────────────────────────────────────
  if (stage >= 6) {
    const synd = dealState.syndicateData || {};
    const price = Number(dealState.rawDealData?.price) || 0;
    const loan  = Number(dealState.lenderMatches?.topRecommendation?.loanAmount) || price * 0.70;
    const gap   = Math.max(0, price - loan);
    if (gap > 0 && !synd.completedAt) {
      alerts.push(alert({
        moduleNumber: 7,
        severity: 'warning',
        category: 'incomplete',
        title: 'Equity gap not addressed',
        message: `A $${Math.round(gap).toLocaleString()} equity gap remains. Complete Module 7 to structure co-investor equity and bridge the capital stack.`,
        actionText: 'Open Syndication Engine',
      }));
    }
  }

  // ── MODULE 8: Loan Package ────────────────────────────────────────────────
  if (stage >= 7) {
    if (!hasField(dealState, 'loanPackage.documentId')) {
      alerts.push(alert({
        moduleNumber: 8,
        severity: 'warning',
        category: 'incomplete',
        title: 'Loan package not generated',
        message: 'No loan package has been created. Generate and review the loan package in Module 8 before outreach to lenders.',
        actionText: 'Generate Loan Package',
      }));
    } else if ((dealState.loanPackage?.completeness || 100) < 80) {
      alerts.push(alert({
        moduleNumber: 8,
        severity: 'warning',
        category: 'missing_doc',
        title: `Loan package incomplete (${dealState.loanPackage.completeness}%)`,
        message: 'The loan package is missing sections. A complete package significantly improves lender approval odds. Review and fill gaps in Module 8.',
        actionText: 'Complete Loan Package',
      }));
    }
  }

  // ── MODULE 9: Outreach ────────────────────────────────────────────────────
  if (stage >= 8) {
    if (!hasField(dealState, 'outreachMaterials.emailTemplates')) {
      alerts.push(alert({
        moduleNumber: 9,
        severity: 'info',
        category: 'incomplete',
        title: 'Lender outreach not sent',
        message: 'Outreach emails to your selected lender have not been generated or sent. Complete Module 9 to draft and dispatch your loan request.',
        actionText: 'Draft Outreach',
      }));
    } else if (!dealState.outreachMaterials?.followUpSequenceCreated) {
      alerts.push(alert({
        moduleNumber: 9,
        severity: 'info',
        category: 'incomplete',
        title: 'Follow-up sequence not created',
        message: 'Lender outreach was sent but no follow-up sequence has been set up. Create Day 3, Day 7, and Day 14 follow-ups in Module 9.',
        actionText: 'Create Follow-up Sequence',
      }));
    }
  }

  // ── MODULE 10: Actions & Follow-Up ───────────────────────────────────────
  if (stage >= 9) {
    const ap = dealState.actionPlan || {};
    if (!ap.tasks?.length) {
      alerts.push(alert({
        moduleNumber: 10,
        severity: 'warning',
        category: 'incomplete',
        title: 'Action plan not created',
        message: 'No action items have been created for this deal. Generate the action plan in Module 10 to track next steps across legal, lender, and due diligence tracks.',
        actionText: 'Create Action Plan',
      }));
    } else {
      const overdue = (ap.tasks || []).filter(t => t.status === 'overdue').length;
      if (overdue > 0) {
        alerts.push(alert({
          moduleNumber: 10,
          severity: 'overdue',
          category: 'deadline',
          persistent: true,
          title: `${overdue} overdue action item${overdue > 1 ? 's' : ''}`,
          message: `${overdue} task${overdue > 1 ? 's are' : ' is'} past deadline in your action plan. Overdue items may delay the deal timeline. Review and update in Module 10.`,
          actionText: 'Review Overdue Tasks',
        }));
      }
    }
  }

  // ── MODULE 11: LOI Generator ──────────────────────────────────────────────
  if (stage >= 10) {
    if (!hasField(dealState, 'loiDocument.documentId')) {
      alerts.push(alert({
        moduleNumber: 11,
        severity: 'warning',
        category: 'missing_doc',
        title: 'LOI not drafted',
        message: 'Letter of Intent has not been created. Draft and send the LOI before proceeding to the PSA and escrow stages.',
        actionText: 'Draft LOI in Module 11',
      }));
    } else if (dealState.loiDocument?.status === 'draft' && stage >= 11) {
      alerts.push(alert({
        moduleNumber: 11,
        severity: 'info',
        category: 'decision',
        title: 'LOI awaiting review',
        message: 'LOI is in draft status. Review the offer price, earnest money, and timeline, then approve and send the LOI to the seller.',
        actionText: 'Review & Approve LOI',
      }));
    }
  }

  // ── MODULE 12: PSA Assistant ──────────────────────────────────────────────
  if (stage >= 11) {
    if (!hasField(dealState, 'psaDocument.documentId')) {
      alerts.push(alert({
        moduleNumber: 12,
        severity: 'warning',
        category: 'missing_doc',
        title: 'PSA not generated',
        message: 'Purchase and Sale Agreement has not been created. Generate the PSA in Module 12 for attorney review before execution.',
        actionText: 'Generate PSA',
      }));
    } else if (dealState.psaDocument?.attorneyReviewRequired && !dealState.psaDocument?.attorneyReviewComplete) {
      alerts.push(alert({
        moduleNumber: 12,
        severity: 'urgent',
        category: 'decision',
        persistent: true,
        title: 'PSA requires attorney review',
        message: 'The PSA contains high-risk clauses that require attorney review before execution. Do not countersign until legal review is complete.',
        actionText: 'Flag for Attorney Review',
      }));
    }
  }

  // ── MODULE 13: Escrow & Title ─────────────────────────────────────────────
  if (stage >= 12) {
    if (!hasField(dealState, 'escrowDetails.escrowNumber')) {
      alerts.push(alert({
        moduleNumber: 13,
        severity: 'urgent',
        category: 'missing_doc',
        title: 'Escrow not opened',
        message: 'Escrow has not been opened with a title company. Open escrow immediately in Module 13 to start the closing timeline.',
        actionText: 'Open Escrow',
        persistent: true,
      }));
    } else {
      // Earnest money deadline
      const earnestDays = daysBetween(dealState.escrowDetails?.earnestDue);
      if (earnestDays !== null && earnestDays <= 3 && earnestDays >= 0) {
        alerts.push(alert({
          moduleNumber: 13,
          severity: 'urgent',
          category: 'deadline',
          persistent: true,
          dismissible: false,
          title: `Earnest money wire due in ${earnestDays} day${earnestDays !== 1 ? 's' : ''}`,
          message: `Earnest money must be wired by ${dealState.escrowDetails.earnestDue}. Verify wire instructions via phone with the escrow officer before sending funds.`,
          actionText: 'Verify & Wire Earnest Money',
        }));
      } else if (earnestDays !== null && earnestDays < 0) {
        alerts.push(alert({
          moduleNumber: 13,
          severity: 'overdue',
          category: 'deadline',
          persistent: true,
          dismissible: false,
          title: 'Earnest money wire OVERDUE',
          message: `The earnest money deadline was ${dealState.escrowDetails.earnestDue}. Contact your escrow officer immediately — failure to fund may jeopardize the contract.`,
          actionText: 'Contact Escrow Officer',
        }));
      }

      // DD deadline
      const ddDays = daysBetween(dealState.escrowDetails?.ddDeadline);
      if (ddDays !== null && ddDays <= 7 && ddDays >= 0) {
        alerts.push(alert({
          moduleNumber: 13,
          severity: ddDays <= 3 ? 'urgent' : 'warning',
          category: 'deadline',
          persistent: ddDays <= 3,
          title: `Due diligence deadline in ${ddDays} day${ddDays !== 1 ? 's' : ''}`,
          message: `Due diligence period closes on ${dealState.escrowDetails.ddDeadline}. Ensure all DD items are cleared or request an extension before the deadline.`,
          actionText: 'Review DD Checklist',
        }));
      }

      // Close date
      const closeDays = daysBetween(dealState.escrowDetails?.closeDate);
      if (closeDays !== null && closeDays <= 14 && closeDays >= 0) {
        alerts.push(alert({
          moduleNumber: 13,
          severity: closeDays <= 5 ? 'urgent' : 'warning',
          category: 'deadline',
          title: `Closing in ${closeDays} day${closeDays !== 1 ? 's' : ''}`,
          message: `Scheduled closing date is ${dealState.escrowDetails.closeDate}. Confirm all financing conditions, title clearance, and wire amounts are set.`,
          actionText: 'Review Closing Checklist',
        }));
      }
    }
  }

  // ── MODULE 14: Due Diligence ──────────────────────────────────────────────
  if (stage >= 13) {
    const dd = dealState.dueDiligence || {};
    const checkedCount = dd.checkedItems?.length || 0;
    const requiredItems = ['zoning', 'licenses', 'insurance', 'bank_stmts', 'tax_returns', 'permit_pull', 'appraisal', 'pest', 'title_search', 'environmental', 'structural', 'rent_roll_verify'];
    const missingItems = requiredItems.filter(item => !dd.checkedItems?.includes(item));

    if (!dd.finalClearanceConfirmed && checkedCount < 8) {
      alerts.push(alert({
        moduleNumber: 14,
        severity: 'urgent',
        category: 'incomplete',
        persistent: true,
        title: `Due diligence incomplete (${checkedCount}/${requiredItems.length} items)`,
        message: `${missingItems.length} due diligence items have not been verified: ${missingItems.slice(0, 3).join(', ')}${missingItems.length > 3 ? ` +${missingItems.length - 3} more` : ''}. Complete all items before clearing for closing.`,
        actionText: 'Complete DD Checklist',
      }));
    } else if (!dd.finalClearanceConfirmed) {
      alerts.push(alert({
        moduleNumber: 14,
        severity: 'warning',
        category: 'decision',
        title: 'Due diligence clearance not confirmed',
        message: 'All DD items have been checked but final clearance has not been confirmed. Review findings and confirm clearance in Module 14 to advance to closing.',
        actionText: 'Confirm DD Clearance',
      }));
    }

    if (dd.riskScoring?.overallRisk === 'High') {
      alerts.push(alert({
        moduleNumber: 14,
        severity: 'urgent',
        category: 'decision',
        persistent: true,
        title: 'High due diligence risk detected',
        message: `DD risk score is High (${dd.riskScoring.score}/100). Review flagged items carefully before proceeding to closing. Consider renegotiating price or requesting seller credits.`,
        actionText: 'Review High-Risk DD Items',
      }));
    }
  }

  // ── MODULE 15: Closing ────────────────────────────────────────────────────
  if (stage >= 14) {
    const closing = dealState.closingDetails || {};
    const completedSteps = closing.completedSteps || [];
    const requiredClosingSteps = ['loan_commitment', 'cd_received', 'cd_approved', 'title_cleared', 'insurance_bound'];
    const missingSteps = requiredClosingSteps.filter(s => !completedSteps.includes(s));

    if (missingSteps.length > 0) {
      alerts.push(alert({
        moduleNumber: 15,
        severity: missingSteps.includes('cd_approved') ? 'urgent' : 'warning',
        category: 'incomplete',
        title: `Closing checklist incomplete (${missingSteps.length} items)`,
        message: `Missing closing steps: ${missingSteps.map(s => s.replace(/_/g, ' ')).join(', ')}. All items must be completed before wiring funds.`,
        actionText: 'Review Closing Checklist',
      }));
    }

    if (!closing.recording?.deedRegistered && completedSteps.length >= 3) {
      alerts.push(alert({
        moduleNumber: 15,
        severity: 'warning',
        category: 'decision',
        title: 'Wire authorization pending',
        message: 'Closing conditions appear met but wire authorization has not been confirmed. Verify wire instructions by phone, then authorize the closing wire in Module 15.',
        actionText: 'Authorize Closing Wire',
      }));
    }
  }

  // ── MODULE 16: Asset Management ───────────────────────────────────────────
  if (stage >= 15) {
    const am = dealState.assetManagement || {};
    if (!am.kpiDashboard?.activatedAt) {
      alerts.push(alert({
        moduleNumber: 16,
        severity: 'info',
        category: 'incomplete',
        title: 'Asset management plan not activated',
        message: 'Property has closed but asset management has not been set up. Configure PM company, fee structure, and KPI dashboard in Module 15.',
        actionText: 'Set Up Asset Management',
      }));
    } else if (!am.managementPlan?.pmCompany) {
      alerts.push(alert({
        moduleNumber: 16,
        severity: 'warning',
        category: 'decision',
        title: 'Property manager not selected',
        message: 'No property management company has been selected. Choose a PM company and configure the management agreement in Module 15.',
        actionText: 'Select Property Manager',
      }));
    }

    if (!am.holdStrategy?.strategy) {
      alerts.push(alert({
        moduleNumber: 16,
        severity: 'info',
        category: 'decision',
        title: 'Hold strategy not defined',
        message: 'No exit or hold strategy has been configured. Define your hold period, target exit cap rate, and strategy (sell/refinance/exchange) in Module 15.',
        actionText: 'Define Hold Strategy',
      }));
    }
  }

  // ── MODULE 17: Exit Strategy ──────────────────────────────────────────────
  if (stage >= 16) {
    const es = dealState.exitStrategy || {};
    if (!es.recommendation) {
      alerts.push(alert({
        moduleNumber: 17,
        severity: 'info',
        category: 'incomplete',
        title: 'Exit analysis not run',
        message: 'No exit scenario has been modeled. Run Module 16 to analyze sell, refinance, and 1031 exchange scenarios, generate IRR projections, and set your exit recommendation.',
        actionText: 'Run Exit Analysis',
      }));
    } else if (!es.completedAt) {
      alerts.push(alert({
        moduleNumber: 17,
        severity: 'info',
        category: 'decision',
        title: 'Exit strategy not finalized',
        message: `Exit recommendation is "${es.recommendation}" but the strategy has not been confirmed. Review IRR projections and finalize your exit plan in Module 16.`,
        actionText: 'Finalize Exit Strategy',
      }));
    }
  }

  // ── Cross-module: blocked pipeline ────────────────────────────────────────
  // Detect if a module is being skipped (stage is behind available modules)
  if (stage > 0 && stage < 17) {
    const nextModule = stage + 1;
    const requiredForNext = getRequiredDataForModule(nextModule, dealState);
    if (requiredForNext.missing.length > 0) {
      alerts.push(alert({
        moduleNumber: nextModule,
        severity: 'warning',
        category: 'blocked',
        title: `Module ${nextModule} is blocked`,
        message: `To advance to Module ${nextModule}, complete the following in Module ${stage}: ${requiredForNext.missing.join(', ')}.`,
        actionText: `Complete Module ${stage}`,
      }));
    }
  }

  // Sort: overdue → urgent → warning → info
  const ORDER = { overdue: 0, urgent: 1, warning: 2, info: 3 };
  return alerts.sort((a, b) => (ORDER[a.severity] ?? 4) - (ORDER[b.severity] ?? 4));
}

// ── Helper: what's needed to enter a given module ─────────────────────────────
function getRequiredDataForModule(moduleNumber, ds) {
  const missing = [];
  switch (moduleNumber) {
    case 2:
      if (!ds.dealId) missing.push('select a deal');
      break;
    case 3:
      if (!ds.marketAnalysis?.marketScore) missing.push('run market analysis');
      break;
    case 4:
      if (!ds.extractedFinancials?.noi) missing.push('extract financials (NOI)');
      break;
    case 5:
      if (!ds.underwritingMetrics?.capRate?.entry) missing.push('run underwriting model');
      break;
    case 6:
      if (!ds.scoreAndOffer?.numericalScore) missing.push('generate deal score');
      break;
    case 7:
      if (!ds.lenderMatches?.topRecommendation?.lenderName) missing.push('select a lender');
      break;
    case 8:
      if (!ds.loanPackage?.documentId) missing.push('generate loan package');
      break;
    case 9:
      if (!ds.outreachMaterials?.emailTemplates?.length) missing.push('complete lender outreach');
      break;
    case 10:
      if (!ds.actionPlan?.tasks?.length) missing.push('create action plan');
      break;
    case 11:
      if (!ds.loiDocument?.documentId) missing.push('draft LOI');
      break;
    case 12:
      if (!ds.psaDocument?.documentId) missing.push('generate PSA');
      break;
    case 13:
      if (!ds.escrowDetails?.escrowNumber) missing.push('open escrow');
      break;
    case 14:
      if (!ds.dueDiligence?.finalClearanceConfirmed) missing.push('confirm DD clearance');
      break;
    case 15:
      if (!ds.closingDetails?.recording?.deedRegistered) missing.push('complete closing');
      break;
    case 16:
      if (!ds.assetManagement?.kpiDashboard?.activatedAt) missing.push('activate asset management');
      break;
    default:
      break;
  }
  return { missing };
}

// ── Severity label helpers ─────────────────────────────────────────────────────
export const SEVERITY_CONFIG = {
  overdue:  { label: 'Overdue',  color: '#0f2d5e', bg: '#eff6ff', border: '#60a5fa', icon: '🔴' },
  urgent:   { label: 'Urgent',   color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', icon: '🔵' },
  warning:  { label: 'Warning',  color: '#1d4ed8', bg: '#eff6ff', border: '#dbeafe', icon: '🔵' },
  info:     { label: 'Info',     color: '#2563eb', bg: '#eff6ff', border: '#93c5fd', icon: '🔵' },
};

export const CATEGORY_LABELS = {
  decision:    'Decision Required',
  missing_doc: 'Missing Document',
  incomplete:  'Incomplete',
  blocked:     'Blocked',
  deadline:    'Deadline',
};

export const MODULE_NAMES = {
  1: 'Deal Lead Generation',   2: 'Market Intelligence',
  3: 'Deal Intake',            4: 'Underwriting Engine',
  5: 'Deal Score & Verdict',   6: 'Lender Match Engine',
  7: 'Loan Package Generator', 8: 'Outreach & Communication',
  9: 'Actions & Follow-up',   10: 'LOI Generator',
  11: 'PSA Assistant',        12: 'Escrow & Title',
  13: 'Due Diligence',        14: 'Closing',
  15: 'Asset Management',     16: 'Exit Strategy',
};
